import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { Deposit } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { parseAmount, validateAmount } from '../../validation'
import { formatAmountInput, formatCzk } from './format'

interface ConfirmPaymentDialogProps {
  open: boolean
  deposit: Deposit | null
  onClose: () => void
  onConfirmed: () => void
}

/**
 * Potvrzení přijetí platby (F3-B/F3-C): obsluha upraví skutečně přijatou částku
 * (předvyplněná sjednanou) a rozhodne, zda klientovi odejde potvrzovací e-mail.
 * Po potvrzení vklad přechází do fronty přidělení jednotek.
 */
export function ConfirmPaymentDialog({
  open,
  deposit,
  onClose,
  onConfirmed,
}: ConfirmPaymentDialogProps) {
  const api = useOpsApi()

  const [amountText, setAmountText] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset polí při každém otevření nad konkrétním vkladem.
  useEffect(() => {
    if (open && deposit) {
      setAmountText(formatAmountInput(deposit.amount))
      setSendEmail(true)
      setError(null)
      setSubmitting(false)
    }
  }, [open, deposit])

  const parsed = parseAmount(amountText)
  const amountError = validateAmount(amountText)
  const differs =
    parsed !== null && parsed > 0 && deposit !== null && parsed !== deposit.amount

  const handleSubmit = async () => {
    if (!deposit || amountError || parsed === null) return
    setSubmitting(true)
    setError(null)
    try {
      await api.confirmPayment(deposit.id, { amountReceived: parsed, sendEmail })
      onConfirmed()
    } catch (err) {
      setError(apiError(err, 'Potvrzení platby selhalo'))
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Potvrdit zaplacení</DialogTitle>
      <DialogContent>
        {deposit && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {deposit.clientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deposit.strategy} · sjednáno {formatCzk(deposit.amount)}
              </Typography>
            </Box>

            <TextField
              size="small"
              autoFocus
              label="Přijatá částka (Kč)"
              value={amountText}
              onChange={(event) => setAmountText(event.target.value)}
              error={Boolean(amountText.trim()) && Boolean(amountError)}
              helperText={amountText.trim() ? amountError ?? undefined : undefined}
              disabled={submitting}
            />

            {differs && (
              <Alert severity="info">
                Přijatá částka se liší od sjednané ({formatCzk(deposit.amount)}).
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(event) => setSendEmail(event.target.checked)}
                  disabled={submitting}
                />
              }
              label="Poslat klientovi potvrzení e-mailem (kopie distributorovi a na klient@apexcapital.cz)"
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || Boolean(amountError)}
        >
          Potvrdit zaplacení
        </Button>
      </DialogActions>
    </Dialog>
  )
}
